import * as React from "react";
import { connect } from "react-redux";
import Checkbox from "@material-ui/core/Checkbox";
import moment from "moment";
import {
  UNDEF,
  SMILEY_VISIT_DETAILS,
  SMILEY_CP_DETAILS,
  API_CANCEL_REVIEW,
} from "Constants";

import "Common.css";
import { cblur, getCOName, isBlank, _attr } from "Util/Utilities";
import "./DialogCompUpdateVisit.css";
import { DateTimeDisplay } from "../DateTimeDisplay";
import {
  parseDate,
  minsToMilitary,
  toISOLocal,
  formatDate,
  formatTime,
  getDurationString,
  convertToDestTZ,
} from "Util/DateUtils";
import { TimePicker } from "Components/CommonComponents";
import {
  getReasons,
  getSimilarVisits,
  getUpcomingVisits,
} from "Actions/VisitActions";
import { TextDropdown } from "../TextDropdown";
import { InputField } from "../InputField";
import BaseElem from "BaseElem";
import { IconButton, Button } from "@material-ui/core";
import Api, { showSnackbar } from "Api";
import { isB2b } from "Screens/ManageCareRecipientB2C/ManageCareRecipient";
import VisitDialog from "./VisitDialog";
import { APP_COUNTRY, isSG } from "DataConstants";
import { toNum } from "Components/VisitUpdateReview/VisitUpdateReview";

export type UpdateVisitProps = {
  base_visit?: any;
  pre_selected_visit_ids?: string[];

  visits: any[];
  customer_request_date?: string;
  reason_for_change?: string;
  reason_for_change_details?: string;
  initiated_by?: string;

  // Cancel visit(s) properties
  reason_for_cancellation?: string;
  reason_for_cancellation_details?: string;
  waive_fee?: boolean;
  waive_fee_reason?: string;
  waive_fee_reason_details?: string;

  // when this is true go directly to pricing engine
  _isPricingUpdate?: boolean;
  _isFromDeeplink?: boolean;
};

export const ADDED_VISITS = "visits";
export const CUSTOMER_REQUEST_DATE = "customer_request_date";
export const INITIATED_BY = "initiated_by";
export const VISIT_IDS = "visit_ids";
export const REASON_FOR_CHANGE = "reason_for_change";
export const IS_PRICING_UPDATE = "_isPricingUpdate";
export const IS_FROM_DEEPLINK = "_isFromDeeplink";
export const REASON_FOR_CHANGE_DETAILS = "reason_for_change_details";

// Cancel visit(s) properties
export const REASON_FOR_CANCELLATION = "reason_for_cancellation";
export const REASON_FOR_CANCELLATION_DETAILS =
  "reason_for_cancellation_details";
export const WAIVE_FEE = "waive_fee";
export const WAIVE_FEE_REASON = "waive_fee_reason";
export const WAIVE_FEE_REASON_DETAILS = "waive_fee_reason_details";
export const CP_COMPENSATION = "cp_compensation";
export const DO_NOT_PAY_CP = "do_not_pay_cp";
export const DO_NOT_PAY_CP_REASON = "do_not_pay_cp_reason";
export const DO_NOT_PAY_CP_DETAILS = "do_not_pay_cp_reason_details";
export const PAY_TRANSPORT_FEE = "pay_cp_transport_fee";

type UVP = {
  ref?: any;
  visible?: boolean;
  data?: UpdateVisitProps;
  recipientStore?: any;
  isUpdate?: boolean;
  updateCallback: Function;

  reasons?: any;
  similarVisits?: any[];
  upcomingVisits?: any[];
};

type UVConnectProps = {
  getReasons: () => void;
  getSimilarVisits: (visitId: string) => void;
  getUpcomingVisits: (visitId: string, excludeVisitId: string) => void;
};

type UVS = {
  // temporarily variable to hold visits until the dropdown is open
  _tempVisits?: any[];

  // hash map [visit_id: cancellation-fee]
  _cancellations?: any;

  // hash map [visitid: carepros array]
  _carepros?: any;
};

// TOOD: add sample output
export function computeDurationString(d1: any, d2: any) {
  const str = getDurationString(d1, d2, true);
  const plusOne = d1 && d2 && d2.getDate() !== d1.getDate() ? "+1 " : "";
  return str.length
    ? `${formatTime(d1)} - ${formatTime(d2)} ${plusOne}(${str})`
    : str;
}

class DialogCompUpdateVisit extends BaseElem<UVP & UVConnectProps, UVS> {
  refOpenSimilarBtn = React.createRef<HTMLDivElement>();
  refVisitDropdown = React.createRef<TextDropdown>();
  isDateUpdated = React.createRef<any>();

  constructor(props: UVP & UVConnectProps) {
    super(props);
    this.state = {
      _cancellations: {},
      _carepros: {},
    };
  }

  // Component maintains an internal state, call cleanup to clear
  _cleanup() {
    this.setState({
      _tempVisits: UNDEF,
    });
  }

  componentDidMount() {
    const self = this;
    const props = self.props;
    props.getReasons();
    const data = props.data;

    const visitId = data && (data[ADDED_VISITS][0] || {})._id;
    if (data && visitId) {
      if (props.isUpdate) {
        props.getSimilarVisits(visitId);
      } else {
        const recipientStore = props.recipientStore;
        const details = recipientStore && recipientStore.careRecipient;
        const cr = details && details.cr;
        cr && props.getUpcomingVisits(cr._id, visitId);
        self._updateCancelFees(data[ADDED_VISITS]);
        if (isSG) {
          this._setDefaultValuesForCPNoShow();
        };
      }
    }
  }

  // TO handle deeplink
  // auto land on cancel/update visits
  componentDidUpdate(prevProps: UVP) {
    const self = this;
    const props = self.props;
    const preSelectedVisits = self.props.data?.pre_selected_visit_ids || [];
    const data = props.data;
    const upcomingVisits = props.upcomingVisits;
    const similarVisits = props.similarVisits;

    let newVisits: any;

    if (prevProps.similarVisits !== similarVisits && similarVisits) {
      newVisits = similarVisits;
    } else if (prevProps.upcomingVisits !== upcomingVisits && upcomingVisits) {
      newVisits = upcomingVisits;
    }

    if (data && !isBlank(newVisits) && !isBlank(preSelectedVisits)) {
      newVisits = newVisits.filter((nv: any) =>
        preSelectedVisits.includes(nv.visit_id)
      );
      data[ADDED_VISITS] = [data[ADDED_VISITS][0]].concat(newVisits);
      self._onSimilarPopupClose();
    }

    // CP No Show
    if (isSG) {
      const prevInitiatedBy = prevProps?.data?.initiated_by;
      const currentInitiatedBy = self.props?.data?.initiated_by;
      if (prevInitiatedBy !== 'homage' && currentInitiatedBy === 'homage') {
        self._setDefaultValueForCancellationDropdown();
      };
    };
  };

  validate(data: UpdateVisitProps) {
    return !!this.getRequestBody(data);
  }

  _setDefaultValuesForCPNoShow = () => {
    const self = this;
    const { data, isUpdate } = this.props;

    const isCPNoShow = data?.base_visit?.status?.visit === 'No Show';

    if (isCPNoShow) {
      const REASON = isUpdate ? REASON_FOR_CHANGE : REASON_FOR_CANCELLATION;
      self._update(INITIATED_BY, "homage", REASON);
    };
  };

  _setDefaultValueForCancellationDropdown = () => {
    const self = this;
    const { data, isUpdate } = this.props;
    const isCPNoShow = data?.base_visit?.status?.visit === 'No Show';
    if (isCPNoShow) {
      const REASON = isUpdate ? REASON_FOR_CHANGE : REASON_FOR_CANCELLATION;
      const item = { value: 'CP No Show' };
      self._update(REASON, item && item.value);
    };
  };


  // returns request body when all fields validated
  getRequestBody(data: UpdateVisitProps) {
    const self = this,
      props = self.props,
      isUpdate = props.isUpdate;
    if (!data) return;

    /*
    {
      "customer_request_date": "2019-11-25T12:00:00.000Z",
      "initiated_by": "careowner",
      "reason_for_cancellation": "Other Reasons",
      "reason_for_cancellation_details": "CR hospitalised",
      "waive_fee": true,
      "waive_fee_reason": "Homage Fault",
      "waive_fee_reason_details": "HO created visit accidently",
      "visits": [
        {"visit_id": "zySv2t8QnWAwxANoo"},
        {"visit_id": "vz8QnWySwxANoo2tA"}
      ]
    }
    */

    const REASON = isUpdate ? REASON_FOR_CHANGE : REASON_FOR_CANCELLATION;
    const REASON_DETAILS = isUpdate
      ? REASON_FOR_CHANGE_DETAILS
      : REASON_FOR_CANCELLATION_DETAILS;

    const initiated_by = data[INITIATED_BY];
    const reason = data[REASON];
    const reason_details = data[REASON_DETAILS];

    if (
      initiated_by === UNDEF ||
      reason === UNDEF ||
      (!(reason_details || "").trim() &&
        self._isDetailsRequired(self._getReasons(), reason))
    ) {
      return;
    }

    const reqBody: any = {};
    reqBody[CUSTOMER_REQUEST_DATE] = data[CUSTOMER_REQUEST_DATE];
    reqBody[INITIATED_BY] = initiated_by;
    reqBody[REASON] = reason;
    reason_details && (reqBody[REASON_DETAILS] = reason_details);

    const waive_fee = data[WAIVE_FEE];
    // for cancel visits
    if (!isUpdate && waive_fee) {
      const cancelFeeWaiverReasons = self._getReasons(true);
      const waive_fee_reason = data[WAIVE_FEE_REASON];
      const waive_fee_reason_details = data[WAIVE_FEE_REASON_DETAILS];

      if (
        waive_fee_reason === UNDEF ||
        (!(waive_fee_reason_details || "").trim() &&
          self._isDetailsRequired(cancelFeeWaiverReasons, waive_fee_reason))
      ) {
        return;
      }

      reqBody[WAIVE_FEE] = !!waive_fee;
      reqBody[WAIVE_FEE_REASON] = waive_fee_reason;
      waive_fee_reason_details &&
        (reqBody[WAIVE_FEE_REASON_DETAILS] = waive_fee_reason_details);
    }

    const visits = data[ADDED_VISITS];
    const baseVisit = visits[0];
    isUpdate && (reqBody["base_visit"] = baseVisit);
    let isValid = true;
    const dontPayCPReasons = self._getDontPayCPReasons();

    reqBody[ADDED_VISITS] = visits.map((v: any) => {
      // for cancel visit(s)
      if (!isUpdate) {
        const visit: any = {
          visit_id: v._id || v.visit_id,
        };

        const compensation = v[CP_COMPENSATION];
        if (compensation) {
          visit[CP_COMPENSATION] = compensation;
          for (let k = compensation.length; k--;) {
            const cpInfo = compensation[k];
            if (!cpInfo[DO_NOT_PAY_CP]) continue;
            const reason = cpInfo[DO_NOT_PAY_CP_REASON];
            if (
              isBlank(reason) ||
              (self._isDetailsRequired(dontPayCPReasons, reason) &&
                isBlank(cpInfo[DO_NOT_PAY_CP_DETAILS]))
            ) {
              isValid = false;
            }
          }
        }

        return visit;
      }

      // for update visits(s)
      if (v === baseVisit) {
        return v;
      }
      const visit_schedule = "visit_schedule";
      const visitCopy = Object.assign({}, baseVisit);
      visitCopy._id = v.visit_id;
      if (visitCopy.urgent_surcharge_package) {
        const visitStart = moment(v.start_date);
        const requestedAr = moment(visitCopy.customer_requested_at);
        const diff = visitStart.diff(requestedAr, "hours", true);
        if (diff > 24) {
          delete visitCopy.urgent_surcharge_package;
        }
      }
      const visitSchedule: any = {};
      visitSchedule.date_from = v.start_date;
      visitSchedule.date_to = v.end_date;
      visitCopy[visit_schedule] = visitSchedule;
      return visitCopy;
    });

    return isValid && reqBody;
  }

  cancelVisits() {
    const self = this,
      props = self.props,
      isUpdate = props.isUpdate;
    if (isUpdate) return;
  }

  _update(propertyName?: string, value?: any, p2Name?: string, p2Val?: any) {
    const self = this;
    const updatedProps: any = { ...self.props.data };
    propertyName && (updatedProps[propertyName] = value);
    p2Name && (updatedProps[p2Name] = p2Val);
    self.props.updateCallback(updatedProps);
    return true;
  }

  _updateTime(militaryTime: string, customerReqDate: Date | undefined) {
    if (!militaryTime || !customerReqDate) return;
    const arr = militaryTime.split(":");
    const hours = parseInt(arr[0]);
    const minutes = parseInt(arr[1]);
    if (hours >= 0 && minutes >= 0) {
      customerReqDate.setHours(hours);
      customerReqDate.setMinutes(minutes);
      customerReqDate.setSeconds(0);
      this._update(CUSTOMER_REQUEST_DATE, toISOLocal(customerReqDate));
    }
  }

  _isVWO() {
    const self = this,
      props = self.props,
      recipientStore = props.recipientStore;
    const details = recipientStore && recipientStore.careRecipient;
    return isB2b(details && details.cr);
  }

  _getReasons(cancelFeeWaiverReasons = false) {
    const props = this.props,
      allReasons = props.reasons,
      isUpdate = props.isUpdate,
      data = props.data;

    if (!allReasons) return [];

    const initiated_by = (data || {}).initiated_by || "careowner";
    let reasons = [];

    try {
      reasons = cancelFeeWaiverReasons
        ? allReasons["cancel_fee_waiver_reasons"][
        initiated_by === "vwo" ? initiated_by : "careowner"
        ]
        : allReasons[isUpdate ? "change_reasons" : "cancel_reasons"][
        initiated_by
        ];
    } catch (e) {
      showSnackbar(
        "API Error: /visits/update/get-reasons\nPlease Contact Tech Team",
        "warning"
      );
    }

    const list: any = [],
      length = reasons.length;
    for (let k = 0; k < length; k++) {
      const r = reasons[k];
      if (r.reasons) {
        list.push({ type: "header", value: r.type });
        list.push(...r.reasons);
        k < length - 1 &&
          list.push({ type: "divider", value: "", key: `d${k}` });
      } else {
        list.push(r);
      }
    }

    return list;
  }

  _getDontPayCPReasons() {
    const props = this.props;
    const allReasons = props.reasons;
    return (
      (allReasons && allReasons["do_not_pay_cp_reasons"]) ||
      [
        /*/TODO: remove
        { value: "Mutual agreement between CO and CP" },
        { value: "Others", is_details_required: true },
        //*/
      ]
    );
  }

  _isDetailsRequired(reasons: any[], value?: string) {
    return !!(
      value &&
      (reasons || []).find(
        (r: any) => r.value === value && r.is_details_required
      )
    );
  }

  _openSimilarDropdown = () => {
    this.refVisitDropdown.current?.focus();
  };

  // similar visits dropdown open callback
  _onSimilarPopupOpen() {
    this.toggleClass("gone", this.refOpenSimilarBtn.current, true);
  }

  // Get cancel fees and descriptions from api and re-render
  _updateCancelFees(visits: any[], initiatedBy?: string) {
    const self = this,
      props = self.props,
      data = props.data;

    // Update fees for cancel visits
    if (!props.isUpdate && data && visits && visits[0]) {
      const reqBody: any = {};
      reqBody[CUSTOMER_REQUEST_DATE] = data[CUSTOMER_REQUEST_DATE];
      reqBody[INITIATED_BY] = initiatedBy || data[INITIATED_BY] || "careowner";
      reqBody[VISIT_IDS] = visits.map((v: any) => v._id || v.visit_id);

      Api.POST(
        API_CANCEL_REVIEW,
        reqBody,
        (resp: any) => {
          if (!resp) return;

          const retVisits = resp.data.visits;
          const cancellations: any = {};
          const carepros: any = {};

          // assign/update cancel fees for all visits
          for (let k = retVisits.length; k--;) {
            const newVisit = retVisits[k];
            cancellations[newVisit.visit_id] = newVisit.cancellation;
            carepros[newVisit.visit_id] = newVisit.carepros;
          }

          self.setState({ _cancellations: cancellations, _carepros: carepros });
        },
        false,
        true,
        true
      );
    }
  }

  // copy temporary visits list back
  _onSimilarPopupClose(inpField?: any) {
    const self = this,
      props = self.props,
      data = props.data;
    if (data) {
      const visitsCopy = (
        self.state._tempVisits ||
        data[ADDED_VISITS] ||
        []
      ).slice();

      const sliced = visitsCopy.slice(1);
      const vd = (v: any) => parseDate(v.start_date).getTime();
      const sorted = sliced.sort((a: any, b: any) => vd(a) - vd(b));
      const visits = [visitsCopy[0]].concat(sorted);

      self._update(ADDED_VISITS, visits);
      self._updateCancelFees(visits);
    }

    if (inpField) {
      self.toggleClass("gone", self.refOpenSimilarBtn.current, false);
    }
    self._cleanup();
  }

  _newVisitSelected(item: any) {
    if (!item) return;
    const self = this;
    let list = self.state._tempVisits || [...self._getSelectedVisits()];

    const idx = list.findIndex((it: any) => it.visit_id === item.visit_id);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.push(item);
    }

    self.setState({ _tempVisits: list });
  }

  _selectAllVisits(visits: any[]) {
    this.setState({ _tempVisits: visits });
  }

  // returns selected visits
  _getSelectedVisits() {
    const self = this,
      props = self.props,
      data = props.data;
    return (data && data[ADDED_VISITS]) || [];
  }

  // remove selected visit (cross button click)
  _removeVisit(visit: any) {
    const self = this,
      data = self.props.data,
      visits = data && data[ADDED_VISITS];
    if (!visits || !visits[0] || !visit) return;

    const filteredVisits = visits.filter((v: any) => v !== visit);
    self._update(ADDED_VISITS, filteredVisits);
    self._updateCancelFees(filteredVisits);
  }

  _haveFees(cancellations?: any) {
    return !!Object.values(cancellations).find(
      (c: any) => parseFloat(c.fee) > 0
    );
  }

  _getCPCompensationObj(visit: any, cpId: string) {
    const compensation = visit[CP_COMPENSATION] || [];
    visit[CP_COMPENSATION] = compensation;
    let compObj = compensation.find((c: any) => c.cp_id === cpId);
    if (!compObj) {
      compObj = { cp_id: cpId };
      compObj[DO_NOT_PAY_CP] = false;

      compensation.push(compObj);
    }
    return compObj;
  }

  _renderVisitItem(
    visit: any,
    onClick: any,
    onMouseEnter: any,
    isSelected: boolean,
    isHovering: boolean
  ) {
    const startDate = parseDate(visit.start_date);
    const endDate = parseDate(visit.end_date);
    const carepros = visit.carepros || [];

    const names =
      carepros
        .map((cp: any) => (cp.role === "Doctor" ? "Dr. " : "") + cp.name)
        .join(", ") || "-";

    return (
      <div
        key={visit.visit_id}
        className="uvc-vdd-item"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        item-selected={isSelected ? "" : UNDEF}
        item-hover={isHovering ? "" : UNDEF}
      >
        <div className="uvc-vdd-col1 vertical">
          <span className="uvc-vdd-date">{formatDate(startDate)}</span>
          <span className="uvc-vdd-time">
            {computeDurationString(startDate, endDate)}
          </span>
        </div>

        <div className="uvc-vdd-col2 elipsis">
          CP Assigned: &nbsp;&nbsp;{names}
        </div>

        {isSelected && (
          <img src="/check_circle.svg" alt="" className="uvc-vdd-item-tick" />
        )}
      </div>
    );
  }

  // CP Payout for urgent visit cancellation
  _getCPPayoutSection(visit: any, cpId: string, cp: any) {
    const self = this;
    //const state = self.state;
    const cpPayout = cp.cp_payout;
    const cpTransportFeePayout = cp.cp_transport_fee_payout;
    const compensation = cpPayout
      ? self._getCPCompensationObj(visit, cpId)
      : UNDEF;

    if (!compensation) return;

    const reasons = self._getDontPayCPReasons();
    const donotPayCP = compensation && compensation[DO_NOT_PAY_CP];

    return (
      <div className="uvc-cp-payout v">
        <span>
          A one hour payment worth <b>{cpPayout}</b> will be paid to the CP
        </span>
        <div
          className="h ha-checkbox-cont m-t-10"
          onClick={() => {
            compensation[DO_NOT_PAY_CP] = !donotPayCP;
            if (!donotPayCP) {
              delete compensation[DO_NOT_PAY_CP_REASON];
              delete compensation[DO_NOT_PAY_CP_DETAILS];
            }
            self._update();
          }}
        >
          <Checkbox
            style={{ display: "block" }}
            checked={!!donotPayCP}
            className="ha-checkbox"
          />
          <span>Do not pay CP</span>
        </div>

        {donotPayCP && (
          <TextDropdown
            showDropdownArrow
            className="uvc-payout-dd"
            inputClassName="uvc-td-inp"
            width="100%"
            popupMaxHeight="280px"
            items={reasons}
            titleProperty="value"
            placeholder="Select a reason"
            selectedItem={compensation[DO_NOT_PAY_CP_REASON]}
            onChange={(_: any, item: any) => {
              if (item) compensation[DO_NOT_PAY_CP_REASON] = item.value;
              else delete compensation[DO_NOT_PAY_CP_REASON];
              self._update();
            }}
          />
        )}

        {donotPayCP &&
          self._isDetailsRequired(
            reasons,
            compensation[DO_NOT_PAY_CP_REASON]
          ) && (
            <InputField
              type="text"
              className="uvc-more-details"
              width="100%"
              multiLine
              noAutoSelect
              maxLines={3}
              lineHeight={19}
              blurOnTabKey
              placeholder="Please specify details"
              onTextChange={(_: any, t: string) => {
                if (t) compensation[DO_NOT_PAY_CP_DETAILS] = t;
                else delete compensation[DO_NOT_PAY_CP_DETAILS];
                self._update();
              }}
              text={compensation[DO_NOT_PAY_CP_DETAILS]}
            />
          )}

        {donotPayCP && cpTransportFeePayout && (
          <div
            className="h ha-checkbox-cont m-t-10"
            onClick={() => {
              compensation[PAY_TRANSPORT_FEE] =
                !compensation[PAY_TRANSPORT_FEE];
              self._update();
            }}
          >
            <Checkbox
              style={{ display: "block" }}
              checked={!!compensation[PAY_TRANSPORT_FEE]}
              className="ha-checkbox"
            />
            <span>Pay transport fee - {cpTransportFeePayout}</span>
          </div>
        )}
      </div>
    );
  }
  
  _renderCancellationFees(cancellationFees: any, index: number) {
    const isGSTIncluded = cancellationFees?.gst_on_visit;
    const cancellationFee = isGSTIncluded
      ? toNum(cancellationFees?.fee) + toNum(cancellationFees?.gst_on_visit)
      : toNum(cancellationFees?.fee);
  
    if (!cancellationFees) return;

    return (
      <div className="uvc-col4" can-remove={index > 0 ? "" : UNDEF}>
        <div className="uvc-cancel-fee">
          {cancellationFee.toFixed(2)} {isGSTIncluded && "(incl. GST)"}
        </div>
        <div className="uvc-cancel-desc">
          {cancellationFees.description}
        </div>
      </div>
    );
  }

  getRegion = () => {
    const { recipientStore } = this.props;
    const timezone = recipientStore?.careRecipient?.cr?.timezone;
    let region = timezone?.timezones?.[APP_COUNTRY];
    if (timezone?.is_single_tz === false) {
      const city = recipientStore?.careRecipient?.co?.address_list?.[0]?.city;
      region = timezone.timezones[city];
    }
    return region;
  };

  render() {
    const self = this,
      state = self.state,
      props = self.props,
      data = props.data;
    if (!data) return null;
    const region = this.getRegion();
    const customer_request_date = data[CUSTOMER_REQUEST_DATE];
    const customerReqDate =
      this.isDateUpdated.current === true
        ? (customer_request_date && parseDate(customer_request_date)) ||
        new Date()
        : (customer_request_date &&
          convertToDestTZ(parseDate(customer_request_date), region)) ||
        new Date();
    const time = minsToMilitary(
      customerReqDate.getHours() * 60 + customerReqDate.getMinutes()
    );
    const initiated_by = data[INITIATED_BY],
      isVWO = self._isVWO();
    const reasons = self._getReasons();
    const isUpdate = props.isUpdate;
    const updateTxt = isUpdate ? "update" : "cancel";
    const changeTxt = isUpdate ? "change" : "cancellation";

    const REASON = isUpdate ? REASON_FOR_CHANGE : REASON_FOR_CANCELLATION;
    const REASON_DETAILS = isUpdate
      ? REASON_FOR_CHANGE_DETAILS
      : REASON_FOR_CANCELLATION_DETAILS;

    const visits = data[ADDED_VISITS],
      baseVisit = visits[0];
    const moreVisits =
      (isUpdate ? props.similarVisits : props.upcomingVisits) || [];
    const haveMoreVisits = !!moreVisits[0];

    const cancellations = state._cancellations;
    const cancellationCPs = state._carepros;
    const cancellation = !isUpdate && cancellations[baseVisit._id];
    const cancelFeeWaiverReasons = !isUpdate && self._getReasons(true);
    const waive_fee = data[WAIVE_FEE];

    return (
      <div className="uvc" data-visible={props.visible ? "" : UNDEF}>
        <span className="uvc-lbl">Client requested for {changeTxt} at:</span>

        <div className="horizontal uvc-date-time">
          <DateTimeDisplay
            inputClassName="uvc-inp"
            singleDateMode
            boldToday
            startDate={convertToDestTZ(customerReqDate, region)}
            dontAllowFutureDates={!window._hack}
            onChange={(date: Date) => {
              date && self._update(CUSTOMER_REQUEST_DATE, toISOLocal(date));
              self.async(() => self._updateCancelFees(visits, initiated_by), 1);
            }}
          />

          <TimePicker
            className="uvc-tp"
            inputClassName="uvc-inp"
            timeEntries={VisitDialog.timeEntries}
            selectedItem={time}
            onChange={(mt: any) => {
              // @ts-ignore
              this.isDateUpdated.current = true;
              self._updateTime(mt, customerReqDate);
              self.async(() => self._updateCancelFees(visits, initiated_by), 1);
            }}
          />
        </div>

        <span className="uvc-lbl uvc-top20">
          Who is initiating the{" "}
          {isUpdate ? "change of visit requirements" : changeTxt}?
        </span>

        <div className="horizontal">
          <button
            className="green-border-icon-btn uvc-btn"
            data-selected={
              initiated_by === "careowner" || initiated_by === "vwo"
                ? ""
                : UNDEF
            }
            onClick={(e: any) => {
              const initiatedBy = isVWO ? "vwo" : "careowner";
              cblur(e);
              self._update(INITIATED_BY, initiatedBy, REASON);
              self._updateCancelFees(visits, initiatedBy);
            }}
          >
            <span>Care Owner / Organisation</span>
          </button>
          <button
            className="green-border-icon-btn uvc-btn"
            style={{ marginLeft: "10px" }}
            data-selected={initiated_by === "homage" ? "" : UNDEF}
            onClick={(e: any) => {
              cblur(e);
              self._update(INITIATED_BY, "homage", REASON);
              self._updateCancelFees(visits, "homage");
            }}
          >
            <span>Homage</span>
          </button>
        </div>

        <span className="uvc-lbl uvc-top20">Reason for {changeTxt}</span>

        <TextDropdown
          showDropdownArrow
          className="uvc-date-time"
          inputClassName="uvc-td-inp"
          width="342px"
          popupWidth="342px"
          popupMaxHeight="280px"
          items={reasons}
          titleProperty="value"
          placeholder={
            isUpdate
              ? "Select most appropriate reason for change"
              : "Select the appropriate reason"
          }
          selectedItem={data[REASON]}
          onChange={(_: any, item: any) =>
            self._update(REASON, item && item.value)
          }
        />

        {self._isDetailsRequired(reasons, data[REASON]) && (
          <InputField
            type="text"
            className="uvc-more-details"
            width="342px"
            multiLine
            noAutoSelect
            maxLines={3}
            lineHeight={19}
            blurOnTabKey
            placeholder="Please specify details"
            onTextChange={(_: any, t: string) =>
              self._update(REASON_DETAILS, t)
            }
            text={data[REASON_DETAILS]}
          />
        )}

        <div className="uvc-visits-head">Visit(s) to {updateTxt}</div>

        <div className="horizontal uvc-gray-labels">
          <div className="uvc-col1">No.</div>
          <div className="uvc-col2" is-cancel={_attr(!isUpdate)}>
            Date and Time
          </div>
          <div className="uvc-col3">Care Pro Assigned</div>
          {cancellation && (
            <div className="uvc-col4">
              Fee and Description ({cancellation.currency})
            </div>
          )}
        </div>

        <div className="uvc-hline uvc-top-8px" />

        {visits.map((v: any, index: number) => {
          const schedule = v.visit_schedule;
          const startDate = parseDate(
            schedule ? schedule.date_from : v.start_date
          );
          const endDate = parseDate(schedule ? schedule.date_to : v.end_date);
          const cancellation = !isUpdate && cancellations[v._id || v.visit_id];
          const cancelCPs = !isUpdate && cancellationCPs[v._id || v.visit_id];
          const assigned_carepros =
            cancelCPs || v.assigned_carepros || v.carepros || [];

          //TODO: testing, remove later
          /*
          assigned_carepros = [
            {
              id: 1,
              role: "Doctor",
              name: "Jenny Cheng",
              avatar_url:
                "https://www.shutterstock.com/blog/wp-content/uploads/sites/5/2019/07/Man-Silhouette.jpg",

              cp_payout: "SGD 20.00",
              cp_transport_fee_payout: "SGD 5.00",
            },
            {
              id: 2,
              role: "Doctor",
              name: "Jenny Cheng",
              avatar_url:
                "https://www.shutterstock.com/blog/wp-content/uploads/sites/5/2019/07/Man-Silhouette.jpg",
            },
          ];
          cancellation = {
            fee: "50.00",
            currency: "SGD",
            description: "CO cancellation within 24 hours of visit start time.",
          };
          // End testing */

          return (
            <div key={v._id || v.visit_id} className="h uvc-vrow">
              <div className="uvc-col1 uvc-gray-labels">{index + 1}</div>
              <div className="uvc-col2 v" is-cancel={!isUpdate ? "" : UNDEF}>
                <span
                  className="uvc-date"
                  onClick={() => window.open(SMILEY_VISIT_DETAILS + v._id)}
                >
                  {formatDate(convertToDestTZ(startDate, region))}
                </span>
                <span className="uvc-time">
                  {computeDurationString(
                    convertToDestTZ(startDate, region),
                    convertToDestTZ(endDate, region)
                  )}
                </span>
              </div>
              <div className="uvc-col3 vertical">
                {!assigned_carepros[0] ? (
                  <div>-</div>
                ) : (
                  assigned_carepros.map((cp: any) => {
                    const profile = (cp && cp.profile) || cp;
                    const id = cp._id || profile.id;
                    const name = profile.first_name
                      ? profile.first_name + " " + profile.last_name
                      : profile.name;
                    const role = cp.role || profile.role || "";
                    const namePrefix = role === "Doctor" ? "Dr. " : "";

                    return (
                      <div className="v uvc-cp-box" key={id || "1"}>
                        <div
                          className="h center "
                          have-link={id}
                          onClick={() =>
                            id && window.open(SMILEY_CP_DETAILS + id)
                          }
                        >
                          <img
                            src={profile.avatar_url || "/carepro.png"}
                            alt={namePrefix + name}
                            className="uvc-cp-av"
                          />
                          <div>
                            <div className="uvc-cp-name">
                              {namePrefix + name}
                            </div>
                            {role && <div>({role})</div>}
                          </div>
                        </div>

                        {self._getCPPayoutSection(v, id, cp)}
                      </div>
                    );
                  })
                )}
              </div>

              {self._renderCancellationFees(cancellation, index)}

              {index > 0 && (
                <IconButton
                  aria-label="Remove Visit"
                  onClick={() => self._removeVisit(v)}
                  className="uvc-remove"
                  size="small"
                >
                  <img src="/close_green.svg" alt="" width="18" />
                </IconButton>
              )}
            </div>
          );
        })}

        <div
          className="uvc-vdd-cont"
          data-disable={!haveMoreVisits ? "" : UNDEF}
        >
          <TextDropdown
            ref={self.refVisitDropdown}
            hidden={!haveMoreVisits}
            className="uvc-vdd"
            inputClassName="uvc-td-inp"
            showDropdownArrow
            width="100%"
            maxWidth="100%"
            popupWidth="606px"
            itemHeight={54}
            //popupWidth={((self.refs.openSimilarBtn || {}) as any).offsetWidth + 'px'}
            popupMaxHeight="307px"
            items={moreVisits}
            idProperty="visit_id"
            multiSelection
            readOnly
            placeholder={"Select more visits to " + updateTxt}
            renderItemFn={self._renderVisitItem}
            selectedItems={self.state._tempVisits || self._getSelectedVisits()}
            onChange={(_: any, item: any) => self._newVisitSelected(item)}
            onFocus={() => self._onSimilarPopupOpen()}
            onBlur={(comp: any) => self._onSimilarPopupClose(comp)}
            leftButton={
              <Button
                tabIndex={0}
                className="ha-dialog-done td-done-all"
                onClick={() =>
                  self._selectAllVisits([baseVisit].concat(moreVisits))
                }
              >
                Select all
              </Button>
            }
          />

          <div
            className="horizontal center uvc-vdd-btn"
            ref={self.refOpenSimilarBtn}
            onClick={self._openSimilarDropdown}
          >
            <img className="uvc-plus" src="/add_circle.svg" alt="" />
            <span className="uvc-more-visits">
              Add more visits to {updateTxt}
            </span>
            {isUpdate && (
              <span className="uvc-more-visits-tooltip">
                Only visits with same requirements can be updated together
              </span>
            )}
          </div>
        </div>

        {isUpdate && visits[1] && (
          <div className="uvc-multiple-tooltip">
            <div>Changes made will be applied to all visits.</div>
            <div>
              You can continue making edits on the next step which will apply to
              all the visits as well.
            </div>
            <div>
              Only the following fields can be edited individually for each
              visit:
            </div>
            <div className="uvc-multiple-inner">
              <div>- Date</div>
              <div>- Number of CPs Required</div>
              <div>- Visit Break</div>
              <div>- Fulfillment Notes</div>
            </div>
          </div>
        )}

        {/* Visit cancel fee waiver section */}
        {!isUpdate && self._haveFees(cancellations) && (
          <div className="uvc-fee-waive-sec">
            <div style={{ marginBottom: "10px" }}>
              Waive fees charged to {getCOName(props.recipientStore)}?
            </div>

            <div className="horizontal grow">
              <button
                className="green-border-icon-btn uvc-small-btn"
                data-selected={!waive_fee ? "" : UNDEF}
                onClick={(e: any) =>
                  cblur(e) && self._update(WAIVE_FEE, false, WAIVE_FEE_REASON)
                }
              >
                <span>No</span>
              </button>
              <button
                className="green-border-icon-btn uvc-small-btn"
                style={{ marginLeft: "10px" }}
                data-selected={waive_fee ? "" : UNDEF}
                onClick={(e: any) =>
                  cblur(e) && self._update(WAIVE_FEE, true, WAIVE_FEE_REASON)
                }
              >
                <span>Yes</span>
              </button>

              {waive_fee && (
                <TextDropdown
                  showDropdownArrow
                  className="uvc-date-time"
                  inputClassName="uvc-td-inp"
                  width="100%"
                  popupWidth="385px"
                  popupMaxHeight="280px"
                  items={cancelFeeWaiverReasons}
                  titleProperty="value"
                  placeholder="Select reason for waiver"
                  selectedItem={data[WAIVE_FEE_REASON]}
                  onChange={(_: any, item: any) =>
                    self._update(WAIVE_FEE_REASON, item && item.value)
                  }
                />
              )}
            </div>

            {self._isDetailsRequired(
              cancelFeeWaiverReasons,
              data[WAIVE_FEE_REASON]
            ) && (
                <InputField
                  type="text"
                  className="uvc-more-details"
                  width="100%"
                  multiLine
                  noAutoSelect
                  maxLines={3}
                  lineHeight={19}
                  blurOnTabKey
                  placeholder="Please specify details"
                  onTextChange={(_: any, t: string) =>
                    self._update(WAIVE_FEE_REASON_DETAILS, t)
                  }
                  text={data[WAIVE_FEE_REASON_DETAILS]}
                />
              )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any, _: UVP) => {
  const { visitStore } = state;
  return {
    ...state,
    reasons: visitStore.reasons,
    similarVisits: visitStore.similarVisits,
    upcomingVisits: visitStore.upcomingVisits,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  getReasons: () => dispatch(getReasons()),
  getSimilarVisits: (visitId: string) => dispatch(getSimilarVisits(visitId)),
  getUpcomingVisits: (crId: string, excludingVisitId: string) =>
    dispatch(getUpcomingVisits(crId, excludingVisitId)),
});

export default connect<UVS, UVConnectProps, UVP>(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(DialogCompUpdateVisit);
